/**
 * Risk Entity - Business Calculations
 * Core business logic for risk scoring, classification, and analytics
 */

import { RISK_LEVELS, REVIEW_FREQUENCIES } from '../model/constants';

/**
 * Calculate risk score (Likelihood × Impact)
 * @param {number} likelihood - Likelihood rating (1-5)
 * @param {number} consequence - Consequence rating (1-5)
 * @returns {number} Risk score (1-25)
 */
export function calculateRiskScore(likelihood, consequence) {
  if (!likelihood || !consequence) return 0;
  if (likelihood < 1 || likelihood > 5 || consequence < 1 || consequence > 5) {
    throw new Error('Likelihood and consequence must be between 1 and 5');
  }
  return likelihood * consequence;
}

/**
 * Determine risk level based on risk score
 * @param {number} riskScore - Risk score (1-25)
 * @returns {string} Risk level (Critical, High, Medium, Low, Very Low)
 */
export function getRiskLevel(riskScore) {
  // RSK-13: mirror apps/risk/models.py::Risk._score_to_level exactly, so the
  // heat map (generateHeatMapData) and the pre-save score previews
  // (RiskForm, RiskScoreCalculator) show the same level the backend will
  // actually persist. The backend has no 'Very Low' tier.
  if (riskScore <= 4) return RISK_LEVELS.LOW.value;
  if (riskScore <= 9) return RISK_LEVELS.MEDIUM.value;
  if (riskScore <= 16) return RISK_LEVELS.HIGH.value;
  return RISK_LEVELS.CRITICAL.value;
}

/**
 * Get risk level configuration object
 * @param {string} riskLevel - Risk level string
 * @returns {Object} Risk level configuration
 */
export function getRiskLevelConfig(riskLevel) {
  const level = Object.values(RISK_LEVELS).find((l) => l.value === riskLevel);
  return level || RISK_LEVELS.VERY_LOW;
}

/**
 * Calculate residual risk score
 * @param {number} residualLikelihood - Residual likelihood (1-5)
 * @param {number} residualConsequence - Residual consequence (1-5)
 * @returns {number} Residual risk score (1-25)
 */
export function calculateResidualRiskScore(residualLikelihood, residualConsequence) {
  if (!residualLikelihood || !residualConsequence) return 0;
  return calculateRiskScore(residualLikelihood, residualConsequence);
}

/**
 * Calculate risk reduction percentage
 * @param {number} inherentScore - Original risk score
 * @param {number} residualScore - Risk score after treatment
 * @returns {number} Reduction percentage (0-100)
 */
export function calculateRiskReduction(inherentScore, residualScore) {
  if (!inherentScore || inherentScore === 0) return 0;
  if (!residualScore) return 0;

  const reduction = ((inherentScore - residualScore) / inherentScore) * 100;
  return Math.max(0, Math.min(100, Math.round(reduction)));
}

/**
 * Calculate next review date based on risk level
 * @param {string} riskLevel - Risk level
 * @param {Date} fromDate - Starting date (defaults to today)
 * @returns {Date} Next review date
 */
export function calculateNextReviewDate(riskLevel, fromDate = new Date()) {
  const days =
    REVIEW_FREQUENCIES[riskLevel.toUpperCase().replace(' ', '_')] || REVIEW_FREQUENCIES.LOW;
  const nextDate = new Date(fromDate);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

/**
 * Check if risk review is overdue
 * @param {Date} reviewDate - Scheduled review date
 * @returns {boolean} True if overdue
 */
export function isReviewOverdue(reviewDate) {
  if (!reviewDate) return false;
  return new Date(reviewDate) < new Date();
}

/**
 * Calculate days until review
 * @param {Date} reviewDate - Scheduled review date
 * @returns {number} Days until review (negative if overdue)
 */
export function daysUntilReview(reviewDate) {
  if (!reviewDate) return null;
  const today = new Date();
  const review = new Date(reviewDate);
  const diffTime = review - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Calculate risk statistics from array of risks
 * @param {Array<Object>} risks - Array of risk objects
 * @returns {Object} Risk statistics
 */
export function calculateRiskStatistics(risks) {
  if (!risks || risks.length === 0) {
    return {
      total: 0,
      open: 0,
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      veryLow: 0,
      averageScore: 0,
      treatmentInProgress: 0,
      overdueTreatments: 0,
    };
  }

  const stats = {
    total: risks.length,
    open: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    veryLow: 0,
    averageScore: 0,
    treatmentInProgress: 0,
    overdueTreatments: 0,
  };

  let totalScore = 0;

  risks.forEach((risk) => {
    // Count by status — backend values: draft, identified, assessed, treated, monitored, closed
    if (!['closed'].includes(risk.status)) stats.open++;
    if (risk.status === 'treated') stats.treatmentInProgress++;

    // Count by risk level (normalized to capitalized: 'Critical', 'High', 'Medium', 'Low')
    switch (risk.inherent_risk_level) {
      case 'Critical':
        stats.critical++;
        break;
      case 'High':
        stats.high++;
        break;
      case 'Medium':
        stats.medium++;
        break;
      case 'Low':
        stats.low++;
        break;
      case 'Very Low':
        stats.veryLow++;
        break;
    }

    // Sum scores
    totalScore += risk.inherent_risk_score || 0;

    // Check for overdue reviews
    if (isReviewOverdue(risk.next_review_date)) {
      stats.overdueTreatments++;
    }
  });

  stats.averageScore = Math.round((totalScore / risks.length) * 10) / 10;

  return stats;
}

/**
 * Generate heat map data from risks
 * @param {Array<Object>} risks - Array of risk objects
 * @returns {Array<Array<Object>>} 5x5 matrix of heat map cells
 */
export function generateHeatMapData(risks) {
  // Initialize 5x5 matrix
  const heatMap = [];
  for (let consequence = 5; consequence >= 1; consequence--) {
    const row = [];
    for (let likelihood = 1; likelihood <= 5; likelihood++) {
      row.push({
        likelihood,
        consequence,
        count: 0,
        risks: [],
        score: calculateRiskScore(likelihood, consequence),
        level: getRiskLevel(calculateRiskScore(likelihood, consequence)),
      });
    }
    heatMap.push(row);
  }

  // Populate with risks
  risks.forEach((risk) => {
    const riskConsequence = risk.inherent_consequence || risk.consequence;
    const riskLikelihood = risk.inherent_likelihood || risk.likelihood;
    if (riskLikelihood && riskConsequence) {
      const rowIndex = 5 - riskConsequence; // Reverse for display (high consequence at top)
      const colIndex = riskLikelihood - 1;

      if (heatMap[rowIndex] && heatMap[rowIndex][colIndex]) {
        heatMap[rowIndex][colIndex].count++;
        heatMap[rowIndex][colIndex].risks.push(risk);
      }
    }
  });

  return heatMap;
}

/**
 * Calculate risk velocity (change in risk score over time)
 * @param {Array<Object>} historicalData - Array of {date, score} objects
 * @returns {number} Velocity (positive = increasing, negative = decreasing)
 */
export function calculateRiskVelocity(historicalData) {
  if (!historicalData || historicalData.length < 2) return 0;

  // Sort by date
  const sorted = [...historicalData].sort((a, b) => new Date(a.date) - new Date(b.date));

  const firstScore = sorted[0].score;
  const lastScore = sorted[sorted.length - 1].score;

  return lastScore - firstScore;
}

/**
 * Determine if risk is within appetite threshold
 * @param {number} riskScore - Risk score
 * @param {number} appetiteThreshold - Organization's risk appetite (default: 12)
 * @returns {boolean} True if within appetite
 */
export function isWithinRiskAppetite(riskScore, appetiteThreshold = 12) {
  return riskScore <= appetiteThreshold;
}

/**
 * Calculate risk exposure (financial impact estimate)
 * @param {number} likelihood - Likelihood rating (1-5)
 * @param {number} financialImpact - Estimated financial impact in currency
 * @returns {number} Risk exposure value
 */
export function calculateRiskExposure(likelihood, financialImpact) {
  if (!likelihood || !financialImpact) return 0;

  // Convert likelihood (1-5) to probability (0.1-0.9)
  const probability = (likelihood / 5) * 0.8 + 0.1;

  return Math.round(probability * financialImpact);
}

/**
 * Prioritize risks based on multiple factors
 * @param {Array<Object>} risks - Array of risk objects
 * @returns {Array<Object>} Sorted array with priority scores
 */
export function prioritizeRisks(risks) {
  return risks
    .map((risk) => {
      let priorityScore = risk.risk_score * 10; // Base score

      // Boost for critical/high risks
      if (risk.risk_level === 'Critical') priorityScore += 50;
      if (risk.risk_level === 'High') priorityScore += 25;

      // Boost for overdue reviews
      if (isReviewOverdue(risk.review_date)) priorityScore += 20;

      // Boost for open status
      if (risk.status === 'Open') priorityScore += 15;

      // Reduce for risks in treatment
      if (risk.status === 'In Treatment') priorityScore -= 10;

      // Reduce for accepted/closed risks
      if (risk.status === 'Accepted' || risk.status === 'Closed') priorityScore -= 50;

      return {
        ...risk,
        priorityScore,
      };
    })
    .sort((a, b) => b.priorityScore - a.priorityScore);
}

/**
 * Group risks by classification (Master Data Level 1, falling back to a
 * legacy risk's old category -- the API's classification_name)
 * @param {Array<Object>} risks - Array of risk objects
 * @returns {Object} Grouped risks by classification
 */
export function groupRisksByClassification(risks) {
  return risks.reduce((groups, risk) => {
    const classification = risk.classification_name || 'Unclassified';
    if (!groups[classification]) {
      groups[classification] = [];
    }
    groups[classification].push(risk);
    return groups;
  }, {});
}

/**
 * Group risks by owner
 * @param {Array<Object>} risks - Array of risk objects
 * @returns {Object} Grouped risks by owner
 */
export function groupRisksByOwner(risks) {
  return risks.reduce((groups, risk) => {
    const owner = risk.owner_name || risk.owner || 'Unassigned';
    if (!groups[owner]) {
      groups[owner] = [];
    }
    groups[owner].push(risk);
    return groups;
  }, {});
}
